<script setup lang="ts">
// components
import Buttons from '~/components/redesign/Buttons';
import ExpandableList from '~/components/ExpandableList.vue';
import SvgIcon from '~/components/SvgIcon.vue';
import MenuItemTitle from './MenuItemTitle.vue';

// composables
import { useLocalePathWithSlash } from '~/composables';

// constants
import MENU from './constants';
import { PATHS } from '~/constants';
import { MODIFIERS } from '~/components/Menu/types';

// utils
import isValidURL from '~/utils/isValidURL';

const localePathWithSlash = useLocalePathWithSlash();

const isExternal = (value: string) => isValidURL(value);
</script>

<template>
  <nav data-component-name="Menu.Mobile">
    <ExpandableList :items="MENU" border-position="bottom">
      <template #heading="{ item }">
        <strong class="heading">{{ $t(item.i18nKey) }}</strong>
      </template>

      <template #content="{ item }">
        <div
          v-for="(column, columnIndex) of item.columns"
          :key="columnIndex"
          class="column"
        >
          <ul>
            <li
              v-for="(item, itemIndex) of column"
              :key="`${columnIndex}-${itemIndex}`"
            >
              <MenuItemTitle v-if="!('path' in item)">
                {{ $t(item.i18nKey) }}
              </MenuItemTitle>

              <MenuItem
                v-else
                :to="localePathWithSlash(item.path)"
                :target="isExternal(item.path) ? '_blank' : '_self'"
                :external="isExternal(item.path)"
              >
                {{ $t(item.i18nKey) }}&nbsp;<sup
                  v-if="item.modifier === MODIFIERS.NEW"
                  class="new"
                >
                  {{ $t('NEW') }}
                </sup>
              </MenuItem>
            </li>
          </ul>
        </div>
      </template>
    </ExpandableList>

    <Buttons.Link :to="localePathWithSlash(PATHS.REQUEST_DEMO.path)">
      {{ $t(PATHS.REQUEST_DEMO.i18nKey) }}
      <SvgIcon name="arrow-forward" />
    </Buttons.Link>
  </nav>
</template>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/mixins/size';
@use '_/mixins/media';
@use '_/fn';

[data-component-name='Menu.Mobile'] {
  overflow-y: auto;
  padding: 0 2rem 2rem 2rem;
  background-color: fn.token('surf-cont-primary');

  display: flex;
  flex-direction: column;

  [data-component-name='ExpandableList'] {
    strong.heading {
      margin-left: 1rem;
      @include typo.subtitle-2;
    }

    .column {
      ul {
        li {
          [data-component-name='Menu.Item'] {
            sup.new {
              color: fn.token('text-error');
              transform: translateY(-0.5rem);
              @include typo.subtitle-3;
            }
          }
        }
      }
    }
  }

  [data-component-name='Buttons.Link'] {
    margin-top: auto;
    align-self: flex-start;
    @include size.fluid-max-width(17.5rem);

    @include media.mobile {
      @include size.fluid-max-width(100%);
    }
  }
}
</style>
